// Horse Overview Tab Component
import React, { useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities, fetchTenantUsers, fetchActivities, fetchConformationNotes, fetchBillingEntities } from '../../lib/queryFunctions';
import { toast } from 'sonner'; // eslint-disable-line no-unused-vars -- used by sub-components indirectly
// SectionHeader, TaskStatusIndicator used by sub-components; coordinator itself doesn't need them directly
import HorsePedigreeCard     from './overview/HorsePedigreeCard';
import HorseVicesCard        from './overview/HorseVicesCard';
import HorseBoardingCard     from './overview/HorseBoardingCard';
import HorseInsuranceCard    from './overview/HorseInsuranceCard';
import HorseTasksSection     from './overview/HorseTasksSection';
import HorseCommsSection     from './overview/HorseCommsSection';
import HorseBasicDetailsCard from './overview/HorseBasicDetailsCard';
import HorseNotesSection     from './overview/HorseNotesSection';

// SectionHeader and TaskStatusIndicator imported from OverviewHelpers — consumed by sub-components

const HorseOverview = ({ horse, onUpdate, tenantId, canEditAccount = true, isFarm = false }) => {
  const qc = useQueryClient();
  const horseId = horse?.id;

  // Bridge ref: HorseNotesSection registers its handleNoteClick here
  // so HorseTasksSection can use it via onOpenNoteDetail prop
  const noteOpenerRef = useRef(null);

  // ── Server state via useQuery ──────────────────────────────────────────────
  const TASK_PARAMS = useMemo(() => ({ activity_type: 'task', horse_id: horseId }), [horseId]);
  const COMM_PARAMS = useMemo(() => ({ activity_type: 'communication', horse_id: horseId }), [horseId]);

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: QK.activities(TASK_PARAMS),
    queryFn: fetchActivities(TASK_PARAMS),
    enabled: !!horseId,
  });

  const { data: communications = [], isLoading: commsLoading } = useQuery({
    queryKey: QK.activities(COMM_PARAMS),
    queryFn: fetchActivities(COMM_PARAMS),
    enabled: !!horseId,
  });

  const { data: conformationNotes = [], isLoading: notesLoading } = useQuery({
    queryKey: QK.conformationNotes(horseId),
    queryFn: fetchConformationNotes(horseId),
    enabled: !!horseId,
  });

  // All CRM entities — boarding/trainer/insurance derived via useMemo (one query, zero duplication)
  const { data: allEntities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
    enabled: !!horseId,
  });
  const boardingFacilities = useMemo(() =>
    allEntities.filter(e => e.organization_industry_role === 'farm_boarding' || e.organization_industry_role === 'farm'),
  [allEntities]);
  const trainerFacilities = useMemo(() =>
    allEntities.filter(e => e.organization_industry_role === 'trainer' || e.person_industry_role === 'trainer'),
  [allEntities]);
  const insuranceContacts = useMemo(() =>
    allEntities.filter(e => e.organization_industry_role === 'insurance' || e.person_industry_role === 'insurance'),
  [allEntities]);
  const accounts = useMemo(() =>
    allEntities.filter(e => e.crm_class === 'account'),
  [allEntities]);

  const { data: tenantUsers = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
    enabled: !!horseId,
  });

  const { data: billingEntities = [] } = useQuery({
    queryKey: QK.billingEntities(),
    queryFn: fetchBillingEntities(),
    enabled: !!horseId,
  });

  // Invalidation helpers
  const invalidateTasks    = () => qc.invalidateQueries({ queryKey: QK.activities(TASK_PARAMS), refetchType: 'all' });
  const invalidateComms    = () => qc.invalidateQueries({ queryKey: QK.activities(COMM_PARAMS), refetchType: 'all' });
  const invalidateNotes    = () => qc.invalidateQueries({ queryKey: QK.conformationNotes(horseId), refetchType: 'all' });
  const invalidateEntities = () => qc.invalidateQueries({ queryKey: QK.crmEntities() });

  return (
    <>
    <div className="grid grid-cols-2 gap-6">
      {/* Left Column */}
      <div className="space-y-6">
        {/* Basic Details — extracted to HorseBasicDetailsCard */}
        <HorseBasicDetailsCard
          horse={horse}
          canEditAccount={canEditAccount}
          onUpdate={onUpdate}
        />

        {/* Pedigree — extracted to sub-component */}
        <HorsePedigreeCard
          horse={horse}
          canEditAccount={canEditAccount}
          onUpdate={onUpdate}
        />

        {/* Vices & Disclaimers — extracted to sub-component */}
        <HorseVicesCard
          horse={horse}
          canEditAccount={canEditAccount}
          onUpdate={onUpdate}
        />

        {/* Boarding Location — extracted to sub-component */}
        <HorseBoardingCard
          horse={horse}
          canEditAccount={canEditAccount}
          onUpdate={onUpdate}
          boardingFacilities={boardingFacilities}
          trainerFacilities={trainerFacilities}
          billingEntities={billingEntities}
          isFarm={isFarm}
          onEntitiesInvalidate={invalidateEntities}
        />

        {/* Insurance — extracted to sub-component */}
        <HorseInsuranceCard
          horse={horse}
          canEditAccount={canEditAccount}
          onUpdate={onUpdate}
          insuranceContacts={insuranceContacts}
          onEntitiesInvalidate={invalidateEntities}
        />
      </div>

      {/* Right Column */}
      <div className="space-y-6">
        {/* Notes — extracted to HorseNotesSection */}
        <HorseNotesSection
          horse={horse}
          canEditAccount={canEditAccount}
          conformationNotes={conformationNotes}
          notesLoading={notesLoading}
          invalidateNotes={invalidateNotes}
          invalidateTasks={invalidateTasks}
          onRegisterNoteOpener={fn => { noteOpenerRef.current = fn; }}
        />

        {/* Tasks — extracted to HorseTasksSection */}
        <HorseTasksSection
          horse={horse}
          tasks={tasks}
          allTasks={tasks}
          tasksLoading={tasksLoading}
          invalidateTasks={invalidateTasks}
          tenantUsers={tenantUsers}
          accounts={accounts}
          conformationNotes={conformationNotes}
          onOpenNoteDetail={note => noteOpenerRef.current?.(note)}
        />

        {/* Communications — extracted to HorseCommsSection */}
        <HorseCommsSection
          horse={horse}
          communications={communications}
          commsLoading={commsLoading}
          invalidateComms={invalidateComms}
          tasks={tasks}
          tenantUsers={tenantUsers}
          accounts={accounts}
        />
      </div>
    </div>
    </>
  );
};

export default HorseOverview;
